import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { HorizontalFlex, PadContainer } from 'smartsuite-ui';
import { inputTypes, predicateValues } from '../constants/constants';
import FilterString from './FilterString';
import FilterSingleSelect from './FilterSingleSelect';
import FilterMultiSelect from './FilterMultiSelect';
import FilterUnconstrainedMultiSelect from './FilterUnconstrainedMultiSelect';
import FilterTreeSelect from './FilterTreeSelect';
import {
    getResetCascadeChildFilter,
    shouldShowFilter,
    getFilterOptions,
    getAlwaysVisibleFilterKeys,
} from './filtersModalUtils';
import DashboardContext from '../hooks/dashboardContext';
import { AdditionalFilterSelector } from './AdditionalFilterSelector';
import { removeFromArray } from '../../viewDashboards/widgets/utils/dataFormatUtils';
import { useLabelContext } from '../hooks/labelContext';

/* eslint-disable complexity */
export default function Filters({
    additionalFilterKeys,
    availableFilters,
    filterValues,
    requiredFilterKeys,
    saveFilters,
}) {
    const { predicateLabels } = useLabelContext();
    const { selectOptions } = useContext(DashboardContext);
    const [usedAdditionalFilterKeys, setUsedAdditionalFilterKeys] = useState(
        additionalFilterKeys.filter((filterKey) =>
            _.keys(filterValues).includes(filterKey)
        )
    );

    function handleAdditionalFilterSelect(selectedFilterOption) {
        if (selectedFilterOption) {
            setUsedAdditionalFilterKeys((prevState) => [
                ...prevState,
                selectedFilterOption.valueField,
            ]);
        }
    }

    function removeAdditionalFilter(filterKey) {
        return () => {
            const updatedFilterValues = { ...filterValues };
            delete updatedFilterValues[filterKey];

            saveFilters({
                ...updatedFilterValues,
                ...getResetCascadeChildFilter(
                    availableFilters[filterKey].cascadeChildKey,
                    updatedFilterValues,
                    predicateLabels
                ),
            });

            setUsedAdditionalFilterKeys((prevState) => {
                const updatedUsedFilterKeys = removeFromArray(
                    prevState,
                    prevState.indexOf(filterKey)
                );

                if (availableFilters[filterKey].cascadeChildKey) {
                    return removeFromArray(
                        updatedUsedFilterKeys,
                        updatedUsedFilterKeys.indexOf(
                            availableFilters[filterKey].cascadeChildKey
                        )
                    );
                }

                return updatedUsedFilterKeys;
            });
        };
    }

    function updateFilter(updatedFilter, cascadeChildKey) {
        saveFilters({
            ...filterValues,
            ...updatedFilter,
            ...getResetCascadeChildFilter(
                cascadeChildKey,
                filterValues,
                predicateLabels
            ),
        });
    }

    function buildFilterComponent(filterKey, isAdditionalFilter) {
        const filter = availableFilters[filterKey];

        const isRequired = filter.isRequired || requiredFilterKeys.includes(filterKey);
        const isAdditionalFilterUsed =
            usedAdditionalFilterKeys.includes(filterKey);

        const cascadeParentFilter = filterValues[filter.cascadeParentKey];
        const disabled = cascadeParentFilter
            ? cascadeParentFilter.values.length > 1 ||
            cascadeParentFilter.predicate.valueField === predicateValues.notIn
            : false;

        const options = getFilterOptions(
            availableFilters,
            filterKey,
            filterValues,
            selectOptions,
            requiredFilterKeys
        );

        if (
            !shouldShowFilter(filter, options, isRequired) ||
            (isAdditionalFilter && !isAdditionalFilterUsed)
        ) {
            return null;
        }

        const selectProps = {
            availableFilter: filter,
            disabled,
            filter: filterValues[filterKey],
            filterKey,
            isAdditionalFilter,
            isRequired,
            key: filterKey,
            options,
            removeAdditionalFilter,
            showAsterisk: false,
            updateFilter,
        };

        if (filter.type === inputTypes.string) {
            return <FilterString {...selectProps} />;
        }
        if (filter.type === inputTypes.singleSelect) {
            return <FilterSingleSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.multiSelect) {
            return <FilterMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.unconstrainedMultiSelect) {
            return <FilterUnconstrainedMultiSelect {...selectProps} />;
        }
        if (filter.type === inputTypes.treeSelect) {
            return <FilterTreeSelect {...selectProps} />;
        }

        return null;
    }

    function buildVisibleFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, false);
    }
    function buildAdditionalFilterComponent(filterKey) {
        return buildFilterComponent(filterKey, true);
    }

    if (_.isEmpty(availableFilters)) {
        return null;
    }

    const alwaysVisibleFilterKeys = getAlwaysVisibleFilterKeys(
        availableFilters,
        additionalFilterKeys
    );
    const visibleFilterComponents = alwaysVisibleFilterKeys
        .map(buildVisibleFilterComponent)
        .filter((x) => x);
    const additionalFilterComponents = usedAdditionalFilterKeys
        .map(buildAdditionalFilterComponent)
        .filter((x) => x);

    const populatedAdditionalFilterKeys = additionalFilterKeys.filter(
        (filterKey) =>
            shouldShowFilter(
                availableFilters[filterKey],
                getFilterOptions(
                    availableFilters,
                    filterKey,
                    filterValues,
                    selectOptions,
                    requiredFilterKeys
                ),
                requiredFilterKeys.includes(filterKey)
            )
    );

    if (
        _.isEmpty(visibleFilterComponents) &&
        !populatedAdditionalFilterKeys.length
    ) {
        return null;
    }

    return (
        <>
            <PadContainer
                cssClassModifiers={['pad-medium', 'no-pad-left-right']}
            >
                <HorizontalFlex
                    cssClassModifiers={['flex-wrap', 'align-flex-end']}
                >
                    {visibleFilterComponents}
                    {additionalFilterComponents}
                    <AdditionalFilterSelector
                        additionalFilterKeys={populatedAdditionalFilterKeys}
                        availableFilters={availableFilters}
                        handleAdditionalFilterSelect={
                            handleAdditionalFilterSelect
                        }
                        isEditor={false}
                        selectOptions={selectOptions}
                        usedAdditionalFilterKeys={usedAdditionalFilterKeys}
                    />
                </HorizontalFlex>
            </PadContainer>
            <hr className="divider divider--criteria" />
        </>
    );
}
/* eslint-enable complexity */

Filters.propTypes = {
    additionalFilterKeys: PropTypes.instanceOf(Array).isRequired,
    availableFilters: PropTypes.instanceOf(Object).isRequired,
    filterValues: PropTypes.instanceOf(Object).isRequired,
    requiredFilterKeys: PropTypes.instanceOf(Array).isRequired,
    saveFilters: PropTypes.func.isRequired,
};
